import type { ReactNode, Ref } from 'react';
import { Icon, type IconName } from '../../components/Icon';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import type { MenuItem } from './menu-items';
import './menu-panel.css';

interface MenuSearchProps {
  readonly inputRef?: Ref<HTMLInputElement> | undefined;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Enter in the search, such as to take the first match. */
  readonly onEnter?: (() => void) | undefined;
  /** What the field is for, when it takes more than a search; "Search" otherwise. */
  readonly label?: string | undefined;
  readonly maxLength?: number | undefined;
}

/** Finds a choice in a menu by its name or what it does. */
export function MenuSearch({
  inputRef,
  value,
  onChange,
  onEnter,
  label,
  maxLength,
}: MenuSearchProps) {
  const { t } = useI18n();
  return (
    <label className="menu-search">
      <Icon name="search" />
      <input
        ref={inputRef}
        type="search"
        className="menu-search-input menu-stop"
        placeholder={label ?? t.menus.search}
        aria-label={label ?? t.menus.search}
        maxLength={maxLength}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || !onEnter) return;
          event.preventDefault();
          onEnter();
        }}
      />
    </label>
  );
}

interface MenuSectionProps {
  readonly label: string;
  /** Beside the heading, such as how many are chosen. */
  readonly aside?: ReactNode;
  readonly children: ReactNode;
}

/** A group of choices under a heading, such as the pinned ones. */
export function MenuSection({ label, aside, children }: MenuSectionProps) {
  return (
    <div className="menu-section" role="group" aria-label={label}>
      <div className="menu-heading-row" aria-hidden="true">
        <p className="menu-heading">{label}</p>
        {aside}
      </div>
      {children}
    </div>
  );
}

/** A choice's icon, or its code: grey for a built-in one, the accent for the writer's own. */
export function MenuIcon({
  item,
}: {
  readonly item: Pick<MenuItem, 'icon' | 'badge' | 'builtIn'>;
}) {
  return (
    <span
      className={cx('menu-row-icon', item.badge && 'menu-row-badge', !item.builtIn && 'is-own')}
      aria-hidden="true"
    >
      {item.badge ?? <Icon name={item.icon} />}
    </span>
  );
}

const rowText = (item: MenuItem) => (
  <span className="menu-row-text">
    <span className="menu-row-name">{item.name}</span>
    <span className="menu-row-hint">{item.hint}</span>
  </span>
);

interface MenuRowProps {
  readonly item: MenuItem;
  readonly onSelect: () => void;
  /** The choice in force, such as the tone, marked with a tick. */
  readonly current?: boolean | undefined;
  /** Toggles whether the choice is kept at the top, without selecting it. */
  readonly onPin?: (() => void) | undefined;
}

/** A choice taken with a click, such as a tool to run or a tone to write in. */
export function MenuRow({ item, onSelect, current, onPin }: MenuRowProps) {
  const { t } = useI18n();
  const pinLabel = `${t.menus.pin}: ${item.name}`;
  return (
    <li className={onPin ? 'menu-choice' : undefined}>
      <button
        type="button"
        className="menu-row menu-stop"
        aria-current={current ? 'true' : undefined}
        onClick={onSelect}
      >
        <MenuIcon item={item} />
        {rowText(item)}
        {current && (
          <span className="menu-row-tick" aria-hidden="true">
            <Icon name="check" />
          </span>
        )}
      </button>
      {onPin && (
        <button
          type="button"
          className="menu-row-pin"
          data-item={item.id}
          aria-label={pinLabel}
          title={pinLabel}
          aria-pressed={item.pinned}
          onClick={onPin}
        >
          <Icon name="pin" />
        </button>
      )}
    </li>
  );
}

interface MenuCheckRowProps {
  readonly item: MenuItem;
  readonly checked: boolean;
  readonly disabled?: boolean | undefined;
  readonly onChange: (checked: boolean) => void;
}

/** A choice switched on or off, several at once, such as the instructions a rewrite follows. */
export function MenuCheckRow({ item, checked, disabled, onChange }: MenuCheckRowProps) {
  return (
    <li>
      <label className={cx('menu-row', checked && 'is-on')}>
        <input
          type="checkbox"
          className="menu-row-checkbox menu-stop"
          checked={checked}
          disabled={disabled}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
        />
        <MenuIcon item={item} />
        {rowText(item)}
        <span className="menu-row-box" aria-hidden="true">
          <Icon name="check" />
        </span>
      </label>
    </li>
  );
}

export interface MenuAction {
  readonly label: string;
  readonly icon: IconName;
  readonly onClick: () => void;
  readonly accent?: boolean;
}

/** What to do next under a menu, and how many of its choices the writer hid. */
export function MenuFooter({
  hidden,
  actions,
}: {
  readonly hidden: number;
  readonly actions: readonly MenuAction[];
}) {
  const { t } = useI18n();
  return (
    <div className="menu-footer">
      {hidden > 0 && <p className="menu-note">{t.menus.hiddenCount(hidden)}</p>}
      <div className="menu-actions">
        {actions.map(({ label, icon, onClick, accent }) => (
          <button
            key={label}
            type="button"
            className={cx('menu-action', accent && 'menu-action--accent')}
            onClick={onClick}
          >
            <Icon name={icon} />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
